import { useState, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb, { getCategoryName } from '@/components/feature/Breadcrumb';
import PageSEO from '@/components/feature/PageSEO';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

const categoryArticleImages: Record<string, string> = {
  '2024-guochanshouji-zipaizhaoxiang-paihangbang': 'https://readdy.ai/api/search-image?query=Top%20view%20of%20modern%20Chinese%20smartphone%20with%20camera%20lens%20visible%2C%20minimalist%20warm%20desk%20setup%2C%20soft%20natural%20lighting%2C%20clean%20cream%20background%2C%20professional%20product%20photography%2C%20elegant%20composition&width=600&height=400&seq=phone-ranking-hero&orientation=landscape',
  'zizai-goutu-sanfenfa-jiqiao': 'https://readdy.ai/api/search-image?query=Smartphone%20showing%20camera%20grid%20lines%20for%20photography%20composition%2C%20warm%20studio%20setting%20with%20natural%20light%2C%20minimalist%20flat%20lay%2C%20cream%20background%2C%20artistic%20photography%20tutorial%20aesthetic&width=600&height=400&seq=composition-rule-of-thirds&orientation=landscape',
  'ziran-guang-zhuajian-zimei': 'https://readdy.ai/api/search-image?query=Beautiful%20natural%20window%20light%20streaming%20into%20minimalist%20room%2C%20smartphone%20on%20tripod%2C%20warm%20golden%20sunlight%2C%20soft%20shadows%2C%20cream%20walls%2C%20cozy%20photography%20studio%20atmosphere&width=600&height=400&seq=natural-light-selfie&orientation=landscape',
  'shouji-xiutu-app-tuijian': 'https://readdy.ai/api/search-image?query=Smartphone%20displaying%20photo%20editing%20app%20interface%20on%20minimalist%20desk%2C%20warm%20ambient%20lighting%2C%20clean%20cream%20background%2C%20professional%20content%20creation%20setup%2C%20modern%20digital%20creative%20workspace&width=600&height=400&seq=photo-editing-apps&orientation=landscape',
  'yeshi-zimei-zhinan': 'https://readdy.ai/api/search-image?query=Smartphone%20capturing%20city%20nightscape%20with%20neon%20lights%20and%20bokeh%20effects%2C%20warm%20urban%20atmosphere%2C%20night%20photography%20scene%2C%20cinematic%20lighting%2C%20modern%20city%20background&width=600&height=400&seq=night-selfie-city&orientation=landscape',
  'xiaohongshu-zimei-yunying': 'https://readdy.ai/api/search-image?query=Creative%20social%20media%20content%20creator%20workspace%20with%20smartphone%20and%20notebook%2C%20warm%20minimalist%20desk%20setup%2C%20soft%20natural%20lighting%2C%20cream%20background%2C%20modern%20digital%20creator%20aesthetic&width=600&height=400&seq=social-media-selfie-blogger&orientation=landscape',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const currentCategory = categories.find((c) => c.slug === slug);
  const currentName = currentCategory ? currentCategory.name : '';

  const filteredArticles = useMemo(() => {
    return articles.filter((a) => a.category === slug);
  }, [slug]);

  const handleCategoryChange = (newSlug: string) => {
    navigate(`/category/${newSlug}`);
  };

  if (!currentCategory) {
    return (
      <>
        <Navbar />
        <div className="pt-20 min-h-screen flex items-center justify-center">
          <p className="text-foreground-500">分类不存在</p>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <PageSEO
        title={currentName}
        description={currentCategory.description}
        keywords={`${currentName},国产自拍,自拍教程,手机拍照`}
        canonicalUrl={`https://www.selfie-guide.cn/category/${slug}`}
        schema={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: `${currentName} - 国产自拍完全指南`,
          description: currentCategory.description,
          url: `https://www.selfie-guide.cn/category/${slug}`,
          mainEntity: {
            '@type': 'ItemList',
            itemListElement: filteredArticles.map((a, idx) => ({
              '@type': 'ListItem',
              position: idx + 1,
              url: `https://www.selfie-guide.cn/article/${a.slug}`,
            })),
          },
        }}
      />
      <Navbar />

      <main className="pt-20 min-h-screen">
        <div className="bg-background-50 border-b border-background-200/70 py-8 md:py-10">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: currentName },
              ]}
            />
            <div className="mt-4">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                {currentName}
              </h1>
              <p className="text-foreground-600 text-sm mt-2">
                {currentCategory.description}
              </p>
            </div>
          </div>
        </div>

        {/* Category Tabs */}
        <div className="bg-background-50 border-b border-background-200/70 sticky top-16 z-30">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-hide">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                    slug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Article List */}
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-10">
          {filteredArticles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {filteredArticles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all duration-300 cursor-pointer"
                >
                  <div className="relative w-full h-44 overflow-hidden">
                    <img
                      src={categoryArticleImages[article.slug] || categoryArticleImages['2024-guochanshouji-zipaizhaoxiang-paihangbang']}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3 px-2.5 py-1 bg-background-50/90 backdrop-blur-sm rounded-full text-xs font-medium text-primary-700">
                      {article.readTime}
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="flex items-center gap-3 text-xs text-foreground-500 mb-2">
                      <span>{article.date}</span>
                      <span>{article.author}</span>
                    </div>
                    <h3 className="font-heading text-base font-semibold text-foreground-950 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                      {article.excerpt}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-secondary-100 mx-auto mb-4">
                <i className="ri-article-line text-secondary-500 text-2xl"></i>
              </div>
              <p className="text-foreground-600 text-sm">该分类暂无文章</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}